import React, { useState } from 'react';
import {
  ExclamationTriangleIcon,
  PlusCircleIcon,
  ArrowPathIcon,
  TrashIcon,
  MinusCircleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

interface SyncPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  syncSummary: {
    new_flows: number;
    modified_flows: number;
    deleted_flows: number;
    total_changes: number;
  };
  details: {
    new_flows: Array<{ id: string; api_name: string; active_version_id?: string; label?: string; type?: string; status?: string; }>;
    modified_flows: Array<{ id: string; api_name: string; active_version_id?: string; label?: string; change_type: string; }>;
    deleted_flows: Array<{ id: string; api_name: string; label: string; active_version_id?: string; }>;
  };
  isSyncing: boolean;
}

export default function SyncPreviewModal({
  isOpen,
  onClose,
  onConfirm,
  syncSummary,
  details,
  isSyncing
}: SyncPreviewModalProps) {
  const [showDetails, setShowDetails] = useState(false);

  if (!isOpen) return null;

  const estimatedTime = Math.ceil(syncSummary.total_changes * 2 / 60); // Rough estimate: 2 seconds per flow

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-xl transform transition-all">
        {/* Header */}
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Sync Preview
          </h3>
        </div>

        {/* Content */}
        <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
          {/* Summary */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Changes Detected:</h4>
            
            <div className="space-y-2">
              {syncSummary.new_flows > 0 && (
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center">
                    <PlusCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                    <span className="text-sm font-medium text-green-900">
                      {syncSummary.new_flows} new flow{syncSummary.new_flows !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <span className="text-xs text-green-700">Will be added with AI summaries</span>
                </div>
              )}

              {syncSummary.modified_flows > 0 && (
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center">
                    <ArrowPathIcon className="h-5 w-5 text-blue-600 mr-2" />
                    <span className="text-sm font-medium text-blue-900">
                      {syncSummary.modified_flows} modified flow{syncSummary.modified_flows !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <span className="text-xs text-blue-700">Will be updated with new summaries</span>
                </div>
              )}

              {syncSummary.deleted_flows > 0 && (
                <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div className="flex items-center">
                    <TrashIcon className="h-5 w-5 text-red-600 mr-2" />
                    <span className="text-sm font-medium text-red-900">
                      {syncSummary.deleted_flows} flow{syncSummary.deleted_flows !== 1 ? 's' : ''} to remove
                    </span>
                  </div>
                  <span className="text-xs text-red-700">No longer active or deleted in Salesforce</span>
                </div>
              )}
            </div>
          </div>

          {/* Estimated Time */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <p className="text-sm text-gray-600">
              <strong>Estimated sync time:</strong> {estimatedTime < 1 ? 'Less than 1' : `~${estimatedTime}`} minute{estimatedTime !== 1 ? 's' : ''}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              AI summaries will be generated in the background after sync completes
            </p>
          </div>

          {/* Toggle Details */}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium mb-4"
          >
            {showDetails ? 'Hide' : 'Show'} detailed changes
          </button>

          {/* Detailed Changes */}
          {showDetails && (
            <div className="space-y-4 text-sm">
              {details.new_flows.length > 0 && (
                <div>
                  <h5 className="font-medium text-gray-700 mb-2">New Flows:</h5>
                  <ul className="space-y-1">
                    {details.new_flows.map(flow => (
                      <li key={flow.id} className="text-gray-600 ml-4">
                        • {flow.label || flow.api_name} ({flow.api_name})
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {details.modified_flows.length > 0 && (
                <div>
                  <h5 className="font-medium text-gray-700 mb-2">Modified Flows:</h5>
                  <ul className="space-y-1">
                    {details.modified_flows.map(flow => (
                      <li key={flow.id} className="text-gray-600 ml-4">
                        • {flow.label || flow.api_name} ({flow.api_name}) - {flow.change_type}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {details.deleted_flows.length > 0 && (
                <div>
                  <h5 className="font-medium text-gray-700 mb-2">Deleted Flows:</h5>
                  <ul className="space-y-1">
                    {details.deleted_flows.map(flow => (
                      <li key={flow.id} className="text-gray-600 ml-4">
                        • {flow.label} ({flow.api_name})
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between">
          <button
            onClick={onClose}
            disabled={isSyncing}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isSyncing}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {isSyncing ? (
              <>
                <ArrowPathIcon className="animate-spin h-4 w-4 mr-2" />
                Syncing...
              </>
            ) : (
              'Confirm Sync'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}